import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import * as XLSX from 'xlsx';
import OpenAPI from '../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../stores/TenantStore';
import { type CustomError } from '../../../models/Error';
import { Icons } from '../../../assets/icons';
import { type components } from '../../../endpoint/Schema';

interface UploadError {
  message: string;
  details?: string;
}

type WhiteListItem = components['schemas']['TenantRegistrationWhitelistDto'];

function WhiteListRegistration() {
  const tenantId = useTenantStore(selectTenantId);
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<UploadError[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Table state
  const [whiteListData, setWhiteListData] = useState<WhiteListItem[]>([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const showToast = useCallback(
    (
      severity: 'success' | 'error' | 'info',
      summary: string,
      detail: string,
    ) => {
      if (toast.current) {
        toast.current.show({
          severity,
          summary,
          detail,
          life: 5000,
        });
      }
    },
    [],
  );

  const downloadSampleFile = useCallback(() => {
    // Create Excel workbook with proper column formatting
    const workbook = XLSX.utils.book_new();

    // Sample data with proper types
    const sampleData = [
      {
        FirstName: 'John',
        LastName: 'Smith',
        Email: 'john.smith@example.co.uk',
        PhoneNumber: '447700900123',
        Birthday: '1990-01-15',
        RenewalDate: '2022-12-01',
        SharedIdentifier: 'SharedIdentifier',
      },
      {
        FirstName: 'Jane',
        LastName: 'Doe',
        Email: 'jane.doe@example.co.uk',
        PhoneNumber: '447700900124',
        Birthday: '1985-03-22',
        RenewalDate: '2023-01-15',
        SharedIdentifier: 'ID002',
      },
      {
        FirstName: 'Alex',
        LastName: 'Johnson',
        Email: 'user@company.com',
        PhoneNumber: '447700900124',
        Birthday: '1992-08-10',
        RenewalDate: '2024-06-30',
        SharedIdentifier: 'REF12345',
      },
    ];

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(sampleData);

    // Set column widths for better readability
    worksheet['!cols'] = [
      { width: 25 }, // Email
      { width: 15 }, // PhoneNumber
      { width: 12 }, // RenewalDate
      { width: 20 }, // SharedIdentifier
      { width: 15 }, // FirstName
      { width: 15 }, // LastName
      { width: 12 }, // Birthday
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'WhiteList');

    // Generate Excel file and download
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'white-list-registration-sample.xlsx');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast(
        'success',
        'Sample Downloaded',
        'Excel template downloaded! All columns are properly formatted - just fill in your data and upload.',
      );
    }
  }, [showToast]);

  const fetchWhiteListData = useCallback(
    async (page: number = 0, rows: number = 10) => {
      if (!tenantId) return;

      setIsLoadingTable(true);
      try {
        const response = await OpenAPI.GET(
          '/api/v2/admin/users/registration-white-list',
          {
            params: {
              query: {
                tenantId,
                skip: page * rows,
                take: rows,
              },
            },
          },
        );

        if (response.data) {
          setWhiteListData(response.data.page ?? []);
          setTotalRecords(response.data.count ?? 0);
        }
      } catch (error) {
        showToast('error', 'Load Failed', 'Failed to load white list data');
      } finally {
        setIsLoadingTable(false);
      }
    },
    [tenantId, showToast],
  );

  // Load data on component mount and after successful upload
  useEffect(() => {
    void fetchWhiteListData(currentPage, rowsPerPage);
  }, [fetchWhiteListData, currentPage, rowsPerPage]);

  const handleFileSelect = useCallback((event: { files: File[] }) => {
    const file = event.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadErrors([]);
      setUploadSuccess(false);
    }
  }, []);

  const handleFileUpload = useCallback(async () => {
    if (!selectedFile || !tenantId) {
      return;
    }

    setIsUploading(true);
    setUploadErrors([]);
    setUploadSuccess(false);

    try {
      const response = await OpenAPI.POST(
        '/api/v2/admin/users/add-user-registration-info',
        {
          params: {
            query: {
              tenantId,
            },
          },
          body: {
            // @ts-expect-error - File type vs string type mismatch in schema
            File: selectedFile,
          },
          bodySerializer: (body) => {
            const formData = new FormData();
            if (body?.File) {
              formData.append('File', body.File as unknown as File);
            }
            return formData;
          },
        },
      );

      // @ts-expect-error
      if (response.error) throw response.error;

      setUploadSuccess(true);
      showToast(
        'success',
        'Upload Successful',
        'Excel file has been processed successfully',
      );

      // Clear the file and component
      setSelectedFile(null);
      if (fileUploadRef.current) {
        fileUploadRef.current.clear();
      }

      // Refresh the table data after successful upload
      void fetchWhiteListData(currentPage, rowsPerPage);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  }, [
    selectedFile,
    tenantId,
    showToast,
    currentPage,
    rowsPerPage,
    fetchWhiteListData,
  ]);

  const clearMessages = useCallback(() => {
    setUploadErrors([]);
    setUploadSuccess(false);
  }, []);

  const clearFile = useCallback(() => {
    setSelectedFile(null);
    setUploadErrors([]);
    setUploadSuccess(false);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  }, []);

  const onPageChange = useCallback(
    (event: { first: number; rows: number; page: number }) => {
      setCurrentPage(event.page);
      setRowsPerPage(event.rows);
    },
    [],
  );

  const formatDate = useCallback((dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateString;
    }
  }, []);

  return (
    <div className="flex flex-col gap-6 w-full">
      <Toast ref={toast} />

      <Card title="White List Registration" className="w-full">
        <div className="flex flex-col gap-4">
          <p className="text-gray-600 mb-4">
            Upload an Excel file containing user registration information. The
            system will process the file and add the users to the white list for
            registration.
          </p>

          {/* Download Sample Button */}
          <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="w-6 h-6 flex items-center justify-center">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-blue-600"
              >
                <path
                  d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">
                Need a template?
              </p>
              <p className="text-xs text-blue-600">
                Download a sample Excel file with the correct format and headers
              </p>
            </div>
            <Button
              label="Download Sample"
              onClick={downloadSampleFile}
              className="p-button-outlined p-button-sm"
              disabled={isUploading}
            />
          </div>

          <div className="flex flex-col gap-4">
            {/* File Selection */}
            <div className="flex flex-col gap-3">
              <FileUpload
                ref={fileUploadRef}
                name="excelFile"
                accept=".xlsx,.xls"
                mode="basic"
                auto={false}
                chooseLabel="Choose Excel File"
                onSelect={handleFileSelect}
                disabled={isUploading}
                className="w-full"
              />

              {selectedFile && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
                  <div className="flex items-center gap-2">
                    <img
                      src={Icons.ic_png_file}
                      alt="file"
                      className="w-5 h-5"
                    />
                    <span className="text-sm font-medium">
                      {selectedFile.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <Button
                    className="p-button-text p-button-sm p-button-rounded"
                    onClick={clearFile}
                    tooltip="Remove file"
                    disabled={isUploading}
                  >
                    ✕
                  </Button>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <div className="flex gap-2">
              <Button
                onClick={handleFileUpload}
                disabled={!selectedFile || isUploading}
                className="p-button-primary"
              >
                <span className="mr-2">⬆</span>
                Upload File
              </Button>
              {selectedFile && !isUploading && (
                <Button onClick={clearFile} className="p-button-secondary">
                  <img
                    src={Icons.ic_delete}
                    alt="delete"
                    className="w-4 h-4 mr-2"
                  />
                  Clear
                </Button>
              )}
            </div>

            {isUploading && (
              <div className="flex items-center gap-2">
                <ProgressSpinner />
                <span>Processing file...</span>
              </div>
            )}

            {uploadSuccess && (
              <Message
                severity="success"
                text="Excel file has been processed successfully!"
                className="w-full"
              />
            )}

            {uploadErrors.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-red-600 font-semibold">Upload Errors:</h4>
                  <Button
                    className="p-button-text p-button-sm"
                    onClick={clearMessages}
                    tooltip="Clear messages"
                  >
                    ✕
                  </Button>
                </div>
                {uploadErrors.map((error, index) => (
                  <Message key={index} severity="error" className="w-full">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">{error.message}</span>
                      {error.details && (
                        <span className="text-sm opacity-80">
                          {error.details}
                        </span>
                      )}
                    </div>
                  </Message>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h5 className="font-semibold mb-2">Excel Format Requirements:</h5>
            <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
              <li>
                The file must be in Excel format (.xlsx or .xls extension)
              </li>
              <li>
                Required columns: <strong>Email</strong>,{' '}
                <strong>PhoneNumber</strong>, <strong>RenewalDate</strong>,{' '}
                <strong>SharedIdentifier</strong>, <strong>FirstName</strong>,{' '}
                <strong>LastName</strong>, <strong>Birthday</strong>
              </li>
              <li>
                Date format should be: YYYY-MM-DD (e.g., 2022-12-01, 1990-01-15)
              </li>
              <li>
                Include headers in the first row exactly as shown in sample
              </li>
              <li>
                Ensure all data fields are properly formatted and complete
              </li>
              <li>File size should be reasonable for processing</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* White List Data Table */}
      <Card title="Registered White List Users" className="w-full">
        <div className="flex flex-col gap-4">
          {isLoadingTable ? (
            <div className="flex items-center justify-center p-8">
              <ProgressSpinner />
              <span className="ml-2">Loading white list data...</span>
            </div>
          ) : (
            <>
              <DataTable
                value={whiteListData}
                responsiveLayout="scroll"
                emptyMessage="No users found in the white list"
                className="p-datatable-sm"
              >
                <Column
                  field="email"
                  header="Email"
                  sortable
                  style={{ minWidth: '200px' }}
                />
                <Column
                  field="phoneNumber"
                  header="Phone Number"
                  body={(rowData: WhiteListItem) => rowData.phoneNumber ?? '-'}
                  style={{ minWidth: '150px' }}
                />
                <Column
                  field="renewalDate"
                  header="Renewal Date"
                  body={(rowData: WhiteListItem) =>
                    formatDate(rowData.renewalDate)
                  }
                  style={{ minWidth: '120px' }}
                />
                <Column
                  field="sharedIdentifier"
                  header="Shared Identifier"
                  body={(rowData: WhiteListItem) =>
                    rowData.sharedIdentifier ?? '-'
                  }
                  style={{ minWidth: '150px' }}
                />
                <Column
                  field="firstName"
                  header="First Name"
                  body={(rowData: WhiteListItem) => rowData.firstName ?? '-'}
                  style={{ minWidth: '120px' }}
                />
                <Column
                  field="lastName"
                  header="Last Name"
                  body={(rowData: WhiteListItem) => rowData.lastName ?? '-'}
                  style={{ minWidth: '120px' }}
                />
                <Column
                  field="birthday"
                  header="Birthday"
                  body={(rowData: WhiteListItem) =>
                    formatDate(rowData.birthday)
                  }
                  style={{ minWidth: '120px' }}
                />
              </DataTable>

              {totalRecords > 0 && (
                <Paginator
                  first={currentPage * rowsPerPage}
                  rows={rowsPerPage}
                  totalRecords={totalRecords}
                  onPageChange={onPageChange}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  className="mt-4"
                />
              )}

              {!isLoadingTable && whiteListData.length === 0 && (
                <div className="text-center p-8 text-gray-500">
                  <p>No users found in the white list.</p>
                  <p className="text-sm mt-2">
                    Upload an Excel file to add users to the white list.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

export default WhiteListRegistration;
